import axios from 'axios'
import firebase from 'firebase/app'
import 'firebase/auth'
import {
  COPAY_FUNCTIONS_BASE,
  fetchCopayPublicConfig,
  isCopayDepositAvailableForCode,
  normalizeCopayVisibleCodes
} from '@/utils/copayPublicConfig'
import { isPaymentBankBlocked } from '@/utils/paymentBankAvailability'
const FINAL_STATUSES = ['SUCCESS', 'FAILED', 'REJECTED', 'EXPIRED', 'CANCELLED', 'CREATE_FAILED', 'MANUAL_REVIEW']
const TRUE_WALLET_BANK_KEYS = ['truewallet', 'truemoney', 'truemoneywallet', 'ทรูวอลเลท', 'ทรูมันนี่']

function normalizeBank (value) {
  return String(value || '').trim().toLowerCase().replace(/[\s_-]+/g, '')
}

function requestId () {
  return `copay-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`
}

export default {
  data () {
    return {
      copayConfig: {
        enabled: false,
        depositEnabled: false,
        availableNow: false,
        depositMin: 300,
        depositMax: 100000,
        visibleToCodes: [],
        blockedBanks: []
      },
      copayConfigLoaded: false,
      copayTransaction: null,
      copayOrderId: '',
      copayStatusTimer: null,
      copayStatusChecking: false,
      copayCancelling: false,
      copayCompletionHandled: false
    }
  },
  computed: {
    hasActiveCopayDeposit () {
      const info = this.copayTransaction && this.copayTransaction.payment_info
      return Boolean(this.copayOrderId && info && (info.promptpay_qr || info.account_no))
    },
    copayDepositRangeText () {
      return `${Number(this.copayConfig.depositMin || 300).toLocaleString()}-${Number(this.copayConfig.depositMax || 100000).toLocaleString()}`
    },
    copayVisibleToUser () {
      const codes = normalizeCopayVisibleCodes(this.copayConfig.visibleToCodes)
      const userCode = String((this.user && this.user.code) || '').trim().toUpperCase()
      return codes.includes('*') || Boolean(userCode && codes.includes(userCode))
    },
    copayDepositAvailableToUser () {
      return isCopayDepositAvailableForCode(this.copayConfig, this.user && this.user.code)
    }
  },
  watch: {
    'user.key': {
      immediate: true,
      handler (userKey) {
        if (!userKey) return
        this.loadCopayConfig()
        this.restoreCopayDeposit()
      }
    }
  },
  beforeDestroy () {
    this.stopCopayStatusPolling()
  },
  methods: {
    async copayHeaders () {
      const currentUser = firebase.auth().currentUser
      if (!currentUser) throw new Error('กรุณาเข้าสู่ระบบใหม่')
      return { Authorization: `Bearer ${await currentUser.getIdToken()}` }
    },
    async loadCopayConfig () {
      try {
        const config = await fetchCopayPublicConfig()
        this.copayConfig = { ...this.copayConfig, ...config }
      } catch (error) {
        console.warn('Copay public config unavailable:', error && error.message ? error.message : error)
      } finally {
        this.copayConfigLoaded = true
      }
    },
    async refreshCopayDepositAvailability () {
      await this.loadCopayConfig()
      return this.copayDepositAvailableToUser
    },
    isCopayAmountAllowed (amount) {
      const value = Number(amount || 0)
      const min = Number(this.copayConfig.depositMin || 300)
      const max = Number(this.copayConfig.depositMax || 100000)
      return Number.isInteger(value) && value >= min && (max <= 0 || value <= max)
    },
    isCopayBlockedDepositAccount (account) {
      return TRUE_WALLET_BANK_KEYS.includes(normalizeBank(account && (account.bank || account.bank_name))) ||
        isPaymentBankBlocked(this.copayConfig.blockedBanks, account)
    },
    async tryCopayDeposit (selectedAccount) {
      if (!this.copayConfigLoaded) await this.loadCopayConfig()
      if (this.isCopayBlockedDepositAccount(selectedAccount)) return false
      if (!this.copayConfig.enabled || !this.copayConfig.depositEnabled || this.copayConfig.availableNow === false || !this.copayVisibleToUser) return false
      const amount = Number.parseInt(this.inputDepositAmount, 10) || 0
      if (!this.isCopayAmountAllowed(amount)) throw new Error(`Copay รองรับยอดฝาก ${this.copayDepositRangeText} บาทต่อรายการ`)
      let response
      try {
        response = await axios.post(`${COPAY_FUNCTIONS_BASE}/copayCreateDeposit`, {
          userKey: this.user.key,
          amount,
          account: selectedAccount,
          bonus: this.getDepositBonusRequested(),
          clientRequestId: requestId()
        }, { headers: await this.copayHeaders(), timeout: 45000 })
      } catch (error) {
        throw new Error(this.copayErrorMessage(error))
      }
      const data = response.data && response.data.data
      if (!response.data || !response.data.success || !data || !data.order_id || !data.payment_info) {
        throw new Error((response.data && response.data.error) || 'Copay response ไม่ครบถ้วน')
      }
      this.depositAmount = amount
      this.depositAmountWithDecimal = amount.toLocaleString('en-US')
      this.depositDecimal = '00'
      this.assignedBank = null
      this.paybridgeMatch = null
      return this.applyCopayTransaction({ ...data, amount_requested: amount })
    },
    copayErrorMessage (error) {
      const payload = error && error.response && error.response.data
      const provider = payload && payload.provider
      return (provider && (provider.message || provider.data)) || (payload && payload.error) || (error && error.message) || 'Copay ไม่สามารถสร้างรายการฝากได้ กรุณาลองใหม่'
    },
    copayStorageKey () {
      return `connextcc_copay_deposit_${(this.user && this.user.key) || 'guest'}`
    },
    persistCopayDeposit (transaction) {
      if (typeof window === 'undefined' || !window.localStorage || !transaction) return
      localStorage.setItem(this.copayStorageKey(), JSON.stringify({
        order_id: transaction.order_id || this.copayOrderId,
        provider_transaction_id: transaction.provider_transaction_id || '',
        amount_requested: Number(transaction.amount_requested || this.depositAmount || 0),
        payment_info: transaction.payment_info || {},
        stored_at: Date.now()
      }))
    },
    clearCopayDepositCache () {
      if (typeof window !== 'undefined' && window.localStorage) localStorage.removeItem(this.copayStorageKey())
    },
    applyCopayTransaction (transaction) {
      if (!transaction || !transaction.order_id || !transaction.payment_info) return false
      this.copayTransaction = { ...(this.copayTransaction || {}), ...transaction }
      this.copayOrderId = transaction.order_id
      this.depositAmount = Number(transaction.amount_requested || transaction.amount || this.depositAmount || 0)
      this.depositAmountWithDecimal = Number(this.depositAmount || 0).toLocaleString('en-US')
      this.assignedBank = null
      this.persistCopayDeposit(this.copayTransaction)
      this.startCopayStatusPolling(transaction.order_id)
      return true
    },
    startCopayStatusPolling (orderId) {
      if (!orderId) return
      this.stopCopayStatusPolling()
      this.copayCompletionHandled = false
      const poll = () => this.fetchCopayDepositStatus(orderId)
      poll()
      this.copayStatusTimer = setInterval(poll, 4000)
    },
    stopCopayStatusPolling () {
      if (this.copayStatusTimer) clearInterval(this.copayStatusTimer)
      this.copayStatusTimer = null
    },
    async fetchCopayDepositStatus (orderId) {
      if (!orderId || !(this.user && this.user.key) || this.copayCompletionHandled || this.copayStatusChecking) return null
      this.copayStatusChecking = true
      try {
        const response = await axios.post(`${COPAY_FUNCTIONS_BASE}/copayGetDepositStatus`, {
          userKey: this.user.key,
          orderId
        }, { headers: await this.copayHeaders(), timeout: 15000 })
        const transaction = response.data && response.data.data
        if (!response.data || !response.data.success || !transaction) return null
        this.copayTransaction = { ...(this.copayTransaction || {}), ...transaction }
        const status = String(transaction.status || '').toUpperCase()
        if (status === 'SUCCESS') this.handleCopayDepositSuccess(transaction)
        else if (FINAL_STATUSES.includes(status)) this.handleCopayDepositFailed(transaction, status)
        return transaction
      } catch (error) {
        return null
      } finally {
        this.copayStatusChecking = false
      }
    },
    async restoreCopayDeposit () {
      if (typeof window === 'undefined' || !window.localStorage || !(this.user && this.user.key)) return false
      try {
        const cached = JSON.parse(localStorage.getItem(this.copayStorageKey()) || 'null')
        if (!cached || !cached.order_id) return false
        const response = await axios.post(`${COPAY_FUNCTIONS_BASE}/copayGetDepositStatus`, {
          userKey: this.user.key,
          orderId: cached.order_id
        }, { headers: await this.copayHeaders(), timeout: 15000 })
        const statusData = response.data && response.data.data
        if (!response.data || !response.data.success || !statusData) {
          this.clearCopayDepositCache()
          return false
        }
        const transaction = { ...cached, ...statusData, order_id: cached.order_id }
        const status = String(transaction.status || '').toUpperCase()
        if (status === 'SUCCESS') return this.handleCopayDepositSuccess(transaction)
        if (FINAL_STATUSES.includes(status)) return this.handleCopayDepositFailed(transaction, status)
        return this.applyCopayTransaction(transaction)
      } catch (error) {
        this.clearCopayDepositCache()
        return false
      }
    },
    handleCopayDepositSuccess (transaction) {
      if (this.copayCompletionHandled) return true
      this.copayCompletionHandled = true
      this.stopCopayStatusPolling()
      this.clearCopayDepositCache()
      const amount = Number(transaction.actual_amount || transaction.amount_requested || this.depositAmount || 0)
      this.resetCopayDepositState()
      if (typeof window !== 'undefined' && window.$) window.$('#depositModal').modal('hide')
      setTimeout(() => this.$swal({ icon: 'success', title: 'ฝากเงินสำเร็จ', text: `ระบบเติมเครดิต ${amount.toLocaleString()} บาทเรียบร้อยแล้ว` }), 200)
      return true
    },
    handleCopayDepositFailed (transaction, status) {
      if (this.copayCompletionHandled) return true
      this.copayCompletionHandled = true
      this.stopCopayStatusPolling()
      this.clearCopayDepositCache()
      const reason = transaction.failed_reason || transaction.credit_error || transaction.copay_error || ''
      this.resetCopayDepositState()
      if (typeof window !== 'undefined' && window.$) window.$('#depositModal').modal('hide')
      setTimeout(() => this.$swal({
        icon: status === 'MANUAL_REVIEW' ? 'warning' : 'error',
        title: status === 'MANUAL_REVIEW' ? 'กำลังตรวจสอบรายการฝาก' : 'รายการฝากไม่สำเร็จ',
        text: typeof reason === 'string' ? (reason || 'กรุณาทำรายการใหม่หรือติดต่อแอดมิน') : 'กรุณาติดต่อแอดมิน'
      }), 200)
      return true
    },
    resetCopayDepositState () {
      this.copayTransaction = null
      this.copayOrderId = ''
      this.copayStatusChecking = false
      this.copayCancelling = false
      this.copayCompletionHandled = false
    },
    async cancelCopayDeposit (options = {}) {
      if (this.copayCancelling) return false
      this.copayCancelling = true
      try {
        const orderId = this.copayOrderId
        if (orderId) await this.fetchCopayDepositStatus(orderId)
        if (!this.copayOrderId) return true
        if (!options.skipConfirmation) {
          const confirmation = await this.$swal({
            icon: 'warning',
            title: 'ยกเลิกรายการและเปลี่ยนยอดฝาก?',
            text: 'หากโอนไปแล้วอย่ายกเลิก กรุณารอระบบยืนยันรายการสำเร็จ',
            showCancelButton: true,
            confirmButtonText: 'ยืนยันยกเลิก',
            cancelButtonText: 'รอทำรายการต่อ'
          })
          if (!confirmation.isConfirmed) return false
        }
        try {
          const response = await axios.post(`${COPAY_FUNCTIONS_BASE}/copayCancelDeposit`, {
            userKey: this.user.key,
            orderId
          }, { headers: await this.copayHeaders(), timeout: 15000 })
          if (!response.data || !response.data.success) throw new Error((response.data && response.data.error) || 'ไม่สามารถปิดรายการฝากได้')
          if (response.data.alreadyCompleted) {
            await this.fetchCopayDepositStatus(orderId)
            return false
          }
        } catch (error) {
          if (this.$swal) await this.$swal({ icon: 'error', title: 'ยังไม่สามารถสร้างรายการใหม่ได้', text: error.message || 'กรุณาลองอีกครั้ง' })
          return false
        }
        this.stopCopayStatusPolling()
        this.clearCopayDepositCache()
        this.resetCopayDepositState()
        this.depositAmount = null
        this.depositAmountWithDecimal = null
        this.inputDepositAmount = null
        return true
      } finally {
        this.copayCancelling = false
      }
    }
  }
}
